import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Clock,
  DollarSign,
  ExternalLink,
  File as FileIcon,
  FileArchive,
  Image as ImageIcon,
  Link as LinkIcon,
  MessageSquare,
  Package,
  Save,
  Send,
  Share2,
  Target,
  UploadCloud,
  Video,
  XCircle,
  Trash2
} from 'lucide-react';
import BriefDetailModal, { BriefDetailType } from '../../components/Profile/brief/BriefDetailModal';
import { useToast, ToastWrapper, useConfirm, ConfirmWrapper } from '../../components/UI/Toast';
import { CATEGORY_TAGS, STYLE_TAGS, TOOL_TAGS, TOPIC_TAGS } from '../../constants/tags';
import { briefService } from '../../services/briefService';
import { mediaUploadService } from '../../services/mediaUploadService';
import { Trial, trialService } from '../../services/trialService';
import { userService } from '../../services/userService';
import { buildShareClipboardText, buildTrialShareText, buildTrialShareUrl, copyTextToClipboard } from '../../utils/share';

const statusConfig: Record<string, { label: string; color: string; icon: React.ComponentType<{ className?: string }> }> = {
  PENDING: { label: '待处理', color: 'text-amber-600 bg-amber-50 border-amber-100', icon: Clock },
  ACCEPTED: { label: '待交付', color: 'text-blue-600 bg-blue-50 border-blue-100', icon: AlertCircle },
  REJECTED: { label: '已拒绝', color: 'text-gray-500 bg-gray-50 border-gray-100', icon: XCircle },
  SUBMITTED: { label: '审核中', color: 'text-purple-600 bg-purple-50 border-purple-100', icon: Clock },
  PASSED: { label: '已通过', color: 'text-green-600 bg-green-50 border-green-100', icon: CheckCircle2 },
  FAILED: { label: '未通过', color: 'text-red-600 bg-red-50 border-red-100', icon: XCircle }
};

type SubmissionKind = 'image' | 'video' | 'archive' | 'url';

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'avif']);
const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'webm', 'm4v', 'avi', 'mpeg', 'mpg']);
const ARCHIVE_EXTENSIONS = new Set(['zip', 'rar', '7z', 'tar', 'gz']);

const getUrlPathname = (url: string) => {
  try {
    return new URL(url).pathname;
  } catch {
    return url.split('?')[0];
  }
};

const getFileExtension = (url: string) => {
  const pathname = getUrlPathname(url);
  const lastSegment = pathname.split('/').pop() || '';
  const dotIndex = lastSegment.lastIndexOf('.');
  if (dotIndex < 0) return '';
  return lastSegment.slice(dotIndex + 1).toLowerCase();
};

const getSubmissionKind = (url: string): SubmissionKind => {
  const ext = getFileExtension(url);
  if (IMAGE_EXTENSIONS.has(ext)) return 'image';
  if (VIDEO_EXTENSIONS.has(ext)) return 'video';
  if (ARCHIVE_EXTENSIONS.has(ext)) return 'archive';
  return 'url';
};

const getSubmissionIcon = (kind: SubmissionKind) => {
  if (kind === 'image') return ImageIcon;
  if (kind === 'video') return Video;
  if (kind === 'archive') return FileArchive;
  return LinkIcon;
};

const getSubmissionLabel = (kind: SubmissionKind) => {
  if (kind === 'image') return '图片资源';
  if (kind === 'video') return '视频资源';
  if (kind === 'archive') return '压缩包资源';
  return '外部链接';
};

const getPlaceholderByState = (role: 'CLIENT' | 'CREATOR', trial: Trial) => {
  if (trial.status === 'PENDING') return '创作者尚未接受试稿。';
  if (trial.status === 'REJECTED') return '该试稿已被拒绝，暂无交付内容。';
  if (role === 'CLIENT' && trial.status === 'ACCEPTED') return '请等待创作者提交交付物。';
  if (role === 'CREATOR' && trial.status === 'SUBMITTED') return '您已提交交付物，请等待需求方审核。';
  if (role === 'CREATOR' && trial.status === 'PASSED') return '该试稿已通过，交付已完成。';
  if (role === 'CREATOR' && trial.status === 'FAILED') return '该试稿未通过，可根据反馈继续调整。';
  return '当前还没有交付内容。';
};

const getDisplayValue = (value?: string, defaultValue: string = '可谈') => {
  if (!value || value.trim() === '') return defaultValue;
  const oldDefaults = ['未提供预算', '未提供工期', '未指定', '待定'];
  if (oldDefaults.includes(value.trim())) return defaultValue;
  return value;
};

const TrialDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  const [trial, setTrial] = useState<Trial | null>(null);
  const [briefDetail, setBriefDetail] = useState<BriefDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentUserRole, setCurrentUserRole] = useState<'CLIENT' | 'CREATOR'>('CLIENT');
  const [selectedBrief, setSelectedBrief] = useState<BriefDetailType | null>(null);

  const [paying, setPaying] = useState(false);

  const [uploadType, setUploadType] = useState<'url' | 'file'>('url');
  const [submissionUrl, setSubmissionUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [feedback, setFeedback] = useState('');
  const [evaluating, setEvaluating] = useState(false);

  const [briefingDraft, setBriefingDraft] = useState({
    deliveryGoals: '',
    clientRemark: ''
  });
  const [savingBriefing, setSavingBriefing] = useState(false);

  useEffect(() => {
    const init = async () => {
      if (!id) return;
      setLoading(true);

      try {
        let currentUserId = '';
        const searchParams = new URLSearchParams(window.location.search);
        const roleParam = searchParams.get('role') || searchParams.get('view');

        const userRes = await userService.getUserProfile();
        if (userRes.success && userRes.data?.userProfile?.userId) {
          currentUserId = userRes.data.userProfile.userId;
        }

        const res = await trialService.getTrialById(id);
        if (!res.success || !res.data?.trial) {
          showToast('无法获取试稿信息', 'error');
          return;
        }

        const nextTrial = res.data.trial;
        setTrial(nextTrial);
        setBriefingDraft({
          deliveryGoals: nextTrial.deliveryGoals || '',
          clientRemark: nextTrial.clientRemark || ''
        });

        try {
          const briefRes = await briefService.getBriefById(nextTrial.briefId);
          const briefRaw = briefRes.data as any;
          const brief = briefRaw?.data?.brief || briefRaw?.brief || briefRaw;
          if (brief) {
            setBriefDetail(brief);
          }
        } catch (briefError) {
          console.error('Failed to load brief detail for trial page:', briefError);
        }

        if (roleParam === 'creator' || roleParam === 'client') {
          setCurrentUserRole(roleParam.toUpperCase() as 'CREATOR' | 'CLIENT');
        } else if (currentUserId) {
          setCurrentUserRole(nextTrial.creatorUserId === currentUserId ? 'CREATOR' : 'CLIENT');
        }
      } catch (error) {
        console.error(error);
        showToast('加载失败', 'error');
      } finally {
        setLoading(false);
      }
    };

    void init();
  }, [id, showToast]);

  const submissionEntries = useMemo(() => {
    return (trial?.submissionUrls || []).map((url) => ({
      url,
      kind: getSubmissionKind(url)
    }));
  }, [trial?.submissionUrls]);

  const previewEntries = useMemo(() => {
    return submissionEntries.filter((entry) => entry.kind === 'image' || entry.kind === 'video');
  }, [submissionEntries]);

  const briefingChanged = trial
    ? briefingDraft.deliveryGoals.trim() !== (trial.deliveryGoals || '')
      || briefingDraft.clientRemark.trim() !== (trial.clientRemark || '')
    : false;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex flex-col pt-24 items-center gap-4">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
        <p className="text-gray-500 font-medium">加载中...</p>
      </div>
    );
  }

  if (!trial) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex flex-col pt-32 items-center text-center px-6">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6">
          <AlertCircle className="w-8 h-8 text-gray-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">试稿未找到</h2>
        <p className="text-gray-500 mb-8">该试稿请求可能已被删除或不存在。</p>
        <button
          onClick={() => navigate('/trials')}
          className="px-6 py-2.5 bg-black text-white rounded-xl font-medium hover:bg-gray-800 transition-colors"
        >
          返回试稿列表
        </button>
      </div>
    );
  }

  const config = statusConfig[trial.status];
  const StatusIcon = config.icon;
  const requirementDescription = briefDetail?.description || trial.description || trial.notes || '客户未提供详细说明，请参考原需求内容。';
  const requirementDeliverables = briefDetail?.deliverables || trial.deliveryGoals || '未指定交付内容';
  const requirementBudget = briefDetail?.budgetRange || trial.budgetRange;
  const requirementDeliveryTime = briefDetail?.deliveryTimeRange;
  const requirementTargetDate = briefDetail?.targetDate;
  const requirementTags = briefDetail?.tags || trial.tags || [];
  const isClientView = currentUserRole === 'CLIENT';
  const isCreatorView = currentUserRole === 'CREATOR';
  const tagGroups = [
    { label: '需求类目', options: CATEGORY_TAGS, colorClass: 'bg-blue-50 text-blue-600 border-blue-100' },
    { label: '风格偏好', options: STYLE_TAGS, colorClass: 'bg-blue-50 text-blue-600 border-blue-100' },
    { label: '文化/题材', options: TOPIC_TAGS, colorClass: 'bg-blue-50 text-blue-600 border-blue-100' },
    { label: '指定工具/模型', options: TOOL_TAGS, colorClass: 'bg-purple-50 text-purple-600 border-purple-100' }
  ]
    .map(({ label, options, colorClass }) => ({
      label,
      colorClass,
      tags: requirementTags.filter((tag) => options.some((option) => option.name === tag))
    }))
    .filter((group) => group.tags.length > 0);

  const handleSubmitWork = async () => {
    let finalUrl = '';

    if (uploadType === 'url') {
      if (!submissionUrl.trim()) {
        showToast('请输入交付物链接', 'error');
        return;
      }
      finalUrl = submissionUrl.trim();
    } else {
      if (!selectedFile) {
        showToast('请选择要上传的文件', 'error');
        return;
      }

      setSubmitting(true);
      try {
        finalUrl = await mediaUploadService.uploadFile(selectedFile, selectedFile.name, selectedFile.type);
      } catch (error) {
        console.error(error);
        showToast('文件上传失败', 'error');
        setSubmitting(false);
        return;
      }
    }

    setSubmitting(true);
    try {
      const res = await trialService.submitTrialWork(trial.id, [finalUrl]);
      if (res.success && res.data?.trial) {
        setTrial(res.data.trial);
        setSubmissionUrl('');
        setSelectedFile(null);
        showToast('交付物已提交，等待需求方审核', 'success');
      } else {
        showToast('提交失败，请重试', 'error');
      }
    } catch (error) {
      console.error(error);
      showToast('提交失败，请重试', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEvaluate = async (status: 'PASSED' | 'FAILED') => {
    if (status === 'FAILED' && !feedback.trim()) {
      showToast('请填写拒绝原因或修改建议', 'error');
      return;
    }

    setEvaluating(true);
    try {
      const res = await trialService.evaluateTrial(trial.id, status, feedback.trim());
      if (res.success && res.data?.trial) {
        setTrial(res.data.trial);
        showToast('试稿审核已更新', 'success');
      } else {
        showToast('操作失败，请重试', 'error');
      }
    } catch (error) {
      console.error(error);
      showToast('操作失败，请重试', 'error');
    } finally {
      setEvaluating(false);
    }
  };

  const handleSaveBriefing = async () => {
    if (!isClientView) return;

    if (!briefingChanged) {
      showToast('交付目标和备注没有变化', 'info');
      return;
    }

    setSavingBriefing(true);
    try {
      const res = await trialService.updateTrialBriefing(trial.id, {
        deliveryGoals: briefingDraft.deliveryGoals.trim(),
        clientRemark: briefingDraft.clientRemark.trim()
      });

      if (res.success && res.data?.trial) {
        setTrial(res.data.trial);
        setBriefingDraft({
          deliveryGoals: res.data.trial.deliveryGoals || '',
          clientRemark: res.data.trial.clientRemark || ''
        });
        showToast('试稿交付目标和备注已更新', 'success');
      } else {
        showToast('保存失败，请稍后重试', 'error');
      }
    } catch (error) {
      console.error(error);
      showToast('保存失败，请稍后重试', 'error');
    } finally {
      setSavingBriefing(false);
    }
  };

  const handleViewBrief = async () => {
    try {
      if (briefDetail) {
        setSelectedBrief(briefDetail);
        return;
      }

      const res = await briefService.getBriefById(trial.briefId);
      const raw = res.data as any;
      const resolvedBrief = raw?.data?.brief || raw?.brief || raw;
      if (resolvedBrief) {
        setBriefDetail(resolvedBrief);
        setSelectedBrief(resolvedBrief);
      } else {
        showToast('无法加载原需求详情', 'error');
      }
    } catch (error) {
      console.error('Failed to load brief detail:', error);
      showToast('无法加载原需求详情', 'error');
    }
  };

  const handleShare = async () => {
    const shareUrl = buildTrialShareUrl(trial.id);
    const shareText = buildTrialShareText();
    await copyTextToClipboard(buildShareClipboardText(shareText, shareUrl));
    showToast('分享链接已复制到剪贴板', 'success');
  };

  const handleCancelTrial = async () => {
    if (!trial) return;
    
    const result = await confirm({
      title: '取消确认',
      message: '确认要取消该试稿吗？取消后试稿记录将被删除。',
      confirmText: '确认取消',
      cancelText: '点错了',
      tone: 'danger'
    });

    if (result.action === 'confirm') {
      try {
        const res = await trialService.deleteTrial(trial.id);
        if (res.success) {
          showToast('试稿已成功取消', 'success');
          setTimeout(() => navigate('/trials'), 1000);
        } else {
          showToast('取消试稿失败，请重试', 'error');
        }
      } catch (error) {
        console.error(error);
        showToast('取消试稿失败，请重试', 'error');
      }
    }
  };

  const handlePay = async () => {
    if (!trial) return;
    setPaying(true);
    try {
      const res = await trialService.payTrial(trial.id);
      if (res.success && res.data?.trial) {
        setTrial(res.data.trial);
        showToast('费用支付成功', 'success');
      } else {
        showToast('支付失败，请重试', 'error');
      }
    } catch (error) {
      console.error(error);
      showToast('支付失败，请重试', 'error');
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] pt-24 pb-20">
      <ToastWrapper />
      <ConfirmWrapper />
      <div className="mx-auto max-w-[1320px] px-6">
        <button
          onClick={() => navigate('/trials')}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors mb-6 group w-fit"
        >
          <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
          <span className="font-medium text-sm">返回试稿列表</span>
        </button>

        <div className="bg-white rounded-[2rem] border border-gray-200 px-8 py-8 mb-6 shadow-sm flex flex-col xl:flex-row justify-between items-start gap-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <div className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full border text-sm font-semibold ${config.color}`}>
                <StatusIcon className="w-4 h-4" />
                {config.label}
              </div>
              <span className="text-sm text-gray-400">ID: {trial.id}</span>
            </div>

            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-slate-900 break-words" title={trial.briefTitle}>
                {trial.briefTitle}
              </h1>
              <div className="flex items-center gap-1">
                <button
                  onClick={handleShare}
                  className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                  title="分享试稿"
                >
                  <Share2 className="w-5 h-5" />
                </button>
                <button
                  onClick={handleCancelTrial}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                  title="取消试稿"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>

            <button
              onClick={handleViewBrief}
              className="mt-3 text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              查看原需求详情
            </button>
          </div>

          <div className="flex items-center gap-8 shrink-0 bg-gray-50 py-4 px-6 rounded-3xl border border-gray-100">
            <div className="text-center">
              <img src={trial.clientAvatar} alt="Client" className="w-14 h-14 rounded-full border-2 border-white shadow-sm mx-auto mb-2 object-cover" />
              <p className="text-sm font-semibold text-gray-900">{trial.clientName}</p>
              <p className="text-xs text-gray-500">客户</p>
            </div>
            <div className="w-px h-12 bg-gray-200"></div>
            <div className="text-center">
              <img src={trial.creatorAvatar} alt="Creator" className="w-14 h-14 rounded-full border-2 border-white shadow-sm mx-auto mb-2 object-cover" />
              <p className="text-sm font-semibold text-gray-900">{trial.creatorName}</p>
              <p className="text-xs text-gray-500">创作者</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2fr)_360px] gap-6 items-start">
          <div className="space-y-6 min-w-0">
            <section className="bg-white rounded-[2rem] border border-gray-200 p-8 shadow-sm">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">需求详情</h3>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap leading-6 mb-6">
                    {requirementDescription}
                  </p>

                  {tagGroups.length > 0 && (
                    <div className="space-y-4 pt-4 border-t border-slate-100">
                      {tagGroups.map((group) => (
                        <div key={group.label}>
                          <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 px-0.5">
                            {group.label}
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {group.tags.map((tag) => (
                              <span
                                key={`${group.label}-${tag}`}
                                className={`px-2 py-0.5 text-xs font-medium rounded border ${group.colorClass}`}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-100 h-full">
                  <h3 className="text-sm font-bold text-blue-900 mb-2 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    交付内容
                  </h3>
                  <p className="text-blue-800 text-sm leading-relaxed whitespace-pre-line">
                    {requirementDeliverables}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-xl border shadow-sm flex items-center gap-3 bg-white border-gray-100">
                  <div className="w-12 h-12 bg-emerald-100 text-emerald-600 flex items-center justify-center rounded-full shrink-0">
                    <DollarSign className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-emerald-600 font-bold uppercase tracking-wide mb-1 opacity-80">预算区间</p>
                    <p className="text-xl font-bold text-gray-900">{getDisplayValue(requirementBudget)}</p>
                  </div>
                </div>

                <div className="p-4 rounded-xl border shadow-sm flex items-center gap-3 bg-white border-gray-100">
                  <div className="w-12 h-12 bg-orange-100 text-orange-600 flex items-center justify-center rounded-full shrink-0">
                    <Calendar className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-orange-600 font-bold uppercase tracking-wide mb-1 opacity-80">预计工期</p>
                    <p className="text-xl font-bold text-gray-900">{getDisplayValue(requirementDeliveryTime)}</p>
                  </div>
                </div>

                <div className="p-4 rounded-xl border shadow-sm flex items-center gap-3 bg-white border-gray-100">
                  <div className="w-12 h-12 bg-purple-100 text-purple-600 flex items-center justify-center rounded-full shrink-0">
                    <Clock className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-purple-600 font-bold uppercase tracking-wide mb-1 opacity-80">目标交付日期</p>
                    <p className="text-xl font-bold text-gray-900">{getDisplayValue(requirementTargetDate)}</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-white rounded-[2rem] border border-gray-200 p-8 shadow-sm">
              <div className="flex flex-col gap-2 mb-8">
                <h2 className="text-lg font-semibold text-slate-900">上传交付物</h2>
                <p className="text-sm text-gray-500">
                  图片和视频会直接展示预览，普通链接和压缩包只保留访问入口。
                </p>
              </div>

              {isCreatorView && trial.status === 'ACCEPTED' && (
                <div className="rounded-3xl border border-gray-100 bg-gray-50 p-5 mb-8">
                  <div className="flex gap-2 p-1 bg-white rounded-2xl w-fit mb-5 border border-gray-100">
                    <button
                      onClick={() => setUploadType('url')}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${uploadType === 'url' ? 'bg-black text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      <LinkIcon className="w-4 h-4" />
                      外链交付
                    </button>
                    <button
                      onClick={() => setUploadType('file')}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${uploadType === 'file' ? 'bg-black text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      <FileIcon className="w-4 h-4" />
                      上传文件
                    </button>
                  </div>

                  {uploadType === 'url' ? (
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">交付物链接</label>
                      <input
                        type="text"
                        value={submissionUrl}
                        onChange={(event) => setSubmissionUrl(event.target.value)}
                        placeholder="输入网盘链接、作品链接或可访问的交付地址"
                        className="w-full bg-white border border-slate-200 text-sm p-4 rounded-2xl focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all placeholder:text-gray-400"
                      />
                    </div>
                  ) : (
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">选择交付文件</label>
                      <input
                        type="file"
                        accept="image/*,video/*,.zip,.rar,.7z,.tar,.gz"
                        onChange={(event) => setSelectedFile(event.target.files ? event.target.files[0] : null)}
                        className="w-full bg-white border border-slate-200 text-sm p-3 rounded-2xl focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                      <p className="text-[11px] text-gray-400 mt-2">支持图片、视频以及 ZIP、RAR、7Z 等压缩包格式。</p>
                    </div>
                  )}

                  <button
                    onClick={handleSubmitWork}
                    disabled={submitting || (uploadType === 'url' ? !submissionUrl.trim() : !selectedFile)}
                    className="w-full bg-black text-white rounded-2xl py-3.5 text-sm font-semibold hover:bg-gray-800 disabled:bg-gray-200 disabled:text-gray-400 transition-all flex items-center justify-center gap-2"
                  >
                    <UploadCloud className="w-4 h-4" />
                    {submitting ? '提交中...' : '提交交付物'}
                  </button>
                </div>
              )}

              {submissionEntries.length > 0 ? (
                <div className="space-y-8">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">交付资源</h3>
                    <div className="space-y-3">
                      {submissionEntries.map((entry, index) => {
                        const ItemIcon = getSubmissionIcon(entry.kind);
                        return (
                          <a
                            key={`${entry.url}-${index}`}
                            href={entry.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-4 rounded-2xl bg-blue-50 border border-blue-100 text-blue-700 hover:bg-blue-100 transition-colors"
                          >
                            <div className="w-10 h-10 rounded-2xl bg-white border border-blue-100 flex items-center justify-center shrink-0">
                              <ItemIcon className="w-4 h-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-blue-500 mb-1">{getSubmissionLabel(entry.kind)}</p>
                              <p className="text-sm font-medium truncate">{entry.url}</p>
                            </div>
                            <ExternalLink className="w-4 h-4 shrink-0" />
                          </a>
                        );
                      })}
                    </div>
                  </div>

                  {previewEntries.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-3">资源预览</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {previewEntries.map((entry, index) => (
                          <div key={`${entry.url}-preview-${index}`} className="rounded-3xl overflow-hidden border border-gray-100 bg-gray-50">
                            {entry.kind === 'image' ? (
                              <img src={entry.url} alt={`trial-preview-${index}`} className="w-full h-[280px] object-cover bg-white" />
                            ) : (
                              <video
                                src={entry.url}
                                controls
                                preload="metadata"
                                className="w-full h-[280px] object-cover bg-black"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-3xl border border-dashed border-gray-200 bg-gray-50 px-8 py-12 text-center">
                  <UploadCloud className="w-10 h-10 mx-auto text-gray-300 mb-4" />
                  <p className="text-sm text-gray-500">{getPlaceholderByState(currentUserRole, trial)}</p>
                </div>
              )}

              {isClientView && trial.status === 'SUBMITTED' && (
                <div className="mt-8 border-t border-gray-100 pt-8">
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">试稿审核反馈</h3>
                  <textarea
                    value={feedback}
                    onChange={(event) => setFeedback(event.target.value)}
                    placeholder="请填写审核意见；如果不通过，必须说明原因或修改建议。"
                    className="w-full bg-slate-50 border border-slate-200 text-sm p-4 rounded-2xl min-h-[120px] resize-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all placeholder:text-gray-400"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                    <button
                      onClick={() => handleEvaluate('FAILED')}
                      disabled={evaluating}
                      className="bg-red-50 text-red-600 border border-red-100 rounded-2xl py-3.5 text-sm font-semibold hover:bg-red-100 disabled:opacity-50 transition-colors"
                    >
                      不予采用
                    </button>
                    <button
                      onClick={() => handleEvaluate('PASSED')}
                      disabled={evaluating}
                      className="bg-green-600 text-white shadow-sm rounded-2xl py-3.5 text-sm font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                      通过并采用
                    </button>
                  </div>
                </div>
              )}

              {(trial.status === 'PASSED' || trial.status === 'FAILED') && (
                <div className={`mt-8 p-5 rounded-3xl border ${trial.status === 'PASSED' ? 'bg-green-50/60 border-green-100 text-green-800' : 'bg-red-50/60 border-red-100 text-red-800'}`}>
                  <div className="flex items-center gap-2 font-semibold mb-2 text-sm">
                    <MessageSquare className="w-4 h-4" />
                    评价反馈
                  </div>
                  <p className="text-sm leading-relaxed opacity-90">{trial.feedback || '需求方未留下额外文字反馈。'}</p>
                </div>
              )}
            </section>
          </div>

          <aside className="xl:sticky xl:top-28 space-y-6">
              <section className="bg-white rounded-[2rem] border border-gray-200 p-7 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                      费用
                    </h3>
                    <span className="text-sm font-bold text-slate-900">
                      {trial.trialFeeAmount && trial.trialFeeAmount > 0 ? `¥${trial.trialFeeAmount.toFixed(2)}` : '免费'}
                    </span>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    trial.paymentStatus === 'PAID' 
                      ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' 
                      : trial.paymentStatus === 'NOT_REQUIRED'
                      ? 'bg-gray-50 text-gray-600 border border-gray-100'
                      : 'bg-amber-50 text-amber-600 border border-amber-100'
                  }`}>
                    {trial.paymentStatus === 'PAID' ? '已支付' : trial.paymentStatus === 'NOT_REQUIRED' ? '无需支付' : '待支付'}
                  </span>
                </div>
                
                {trial.paymentStatus !== 'PAID' && trial.paymentStatus !== 'NOT_REQUIRED' && isClientView && trial.status === 'ACCEPTED' && (
                  <button
                    onClick={handlePay}
                    disabled={paying}
                    className="w-full bg-emerald-500 text-white rounded-2xl py-3.5 text-sm font-semibold hover:bg-emerald-600 disabled:opacity-50 transition-colors"
                  >
                    {paying ? '支付中...' : '立即支付'}
                  </button>
                )}
                
                {trial.paymentStatus !== 'PAID' && trial.paymentStatus !== 'NOT_REQUIRED' && isClientView && trial.status !== 'ACCEPTED' && (
                  <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded-xl border border-gray-100">
                    仅在创作者接受试稿后（待交付状态），才可以进行支付。
                  </p>
                )}


              </section>

            <section className="bg-white rounded-[2rem] border border-gray-200 p-7 shadow-sm">
              <div className="space-y-5">
                <div className="rounded-3xl border border-gray-100 bg-gray-50 p-5">
                  <div className="flex items-center gap-2 text-gray-900 mb-3">
                    <Target className="w-4 h-4" />
                    <h3 className="text-lg font-semibold text-slate-900">试稿交付目标</h3>
                  </div>
                  {isClientView ? (
                    <textarea
                      value={briefingDraft.deliveryGoals}
                      onChange={(event) => setBriefingDraft((prev) => ({ ...prev, deliveryGoals: event.target.value }))}
                      placeholder="填写试稿交付目标，例如成片规格、画面重点、需要输出的文件类型等。"
                      className="w-full bg-white border border-slate-200 text-sm p-4 rounded-2xl min-h-[140px] resize-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all placeholder:text-gray-400"
                    />
                  ) : (
                    <p className="text-sm leading-6 text-slate-700 whitespace-pre-wrap">
                      {trial.deliveryGoals || '需求方暂未补充具体交付目标。'}
                    </p>
                  )}
                </div>

                <div className="rounded-3xl border border-gray-100 bg-gray-50 p-5">
                  <div className="flex items-center gap-2 text-gray-900 mb-3">
                    <MessageSquare className="w-4 h-4" />
                    <h3 className="text-lg font-semibold text-slate-900">试稿备注</h3>
                  </div>
                  {isClientView ? (
                    <textarea
                      value={briefingDraft.clientRemark}
                      onChange={(event) => setBriefingDraft((prev) => ({ ...prev, clientRemark: event.target.value }))}
                      placeholder="填写补充备注，例如优先级、禁用元素、素材口径或沟通提醒。"
                      className="w-full bg-white border border-slate-200 text-sm p-4 rounded-2xl min-h-[140px] resize-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all placeholder:text-gray-400"
                    />
                  ) : (
                    <p className="text-sm leading-6 text-slate-700 whitespace-pre-wrap">
                      {trial.clientRemark || '需求方暂未补充新的试稿备注。'}
                    </p>
                  )}
                </div>
              </div>

              {isClientView && (
                <button
                  onClick={handleSaveBriefing}
                  disabled={savingBriefing || !briefingChanged}
                  className="w-full mt-6 bg-black text-white rounded-2xl py-3.5 text-sm font-semibold hover:bg-gray-800 disabled:bg-gray-200 disabled:text-gray-400 transition-all flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {savingBriefing ? '保存中...' : '保存交付目标与备注'}
                </button>
              )}

              {!isClientView && (
                <div className="mt-6 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                  需求方更新后，您会收到系统提醒。
                </div>
              )}

              {isCreatorView && trial.status === 'SUBMITTED' && (
                <div className="mt-6 rounded-2xl border border-purple-100 bg-purple-50 px-4 py-3 text-sm text-purple-700 flex items-start gap-2">
                  <Send className="w-4 h-4 mt-0.5 shrink-0" />
                  您已完成交付，当前正在等待需求方审核。
                </div>
              )}
            </section>
          </aside>
        </div>
      </div>

      {selectedBrief && (
        <BriefDetailModal
          isOpen={!!selectedBrief}
          onClose={() => setSelectedBrief(null)}
          brief={selectedBrief}
        />
      )}
    </div>
  );
};

export default TrialDetail;
